import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { useStickyState } from "@/hooks/useStickyState";
import { cn } from "@/lib/utils";

/** Fired by the internal UI tab's "Reset Dismissed State" button. */
export const FEATURE_BANNER_RESET_EVENT = "feature-banner-reset";

interface NewFeatureBannerProps {
  id: string;
  title: string;
  description: string;
  ctaLabel?: string;
  onCtaClick?: () => void;
}

export default function NewFeatureBanner({
  id,
  title,
  description,
  ctaLabel,
  onCtaClick,
}: NewFeatureBannerProps) {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useStickyState(`feature-banner-dismissed-${id}`, false);
  const [animatingOut, setAnimatingOut] = useState(false);

  // The reset button clears localStorage, but this component is already mounted
  // with `dismissed: true` in state — bring it back without a page reload.
  useEffect(() => {
    const onReset = () => {
      setAnimatingOut(false);
      setDismissed(false);
    };
    window.addEventListener(FEATURE_BANNER_RESET_EVENT, onReset);
    return () => window.removeEventListener(FEATURE_BANNER_RESET_EVENT, onReset);
  }, [setDismissed]);

  if (dismissed) return null;

  const handleDismiss = () => {
    setAnimatingOut(true);
    setTimeout(() => setDismissed(true), 300);
  };

  const ctaHref = "https://www.coffee.ai/brew-notes";

  return (
    <div
      className={cn(
        "relative w-full overflow-hidden px-4 py-2 transition-all duration-300 shrink-0",
        "bg-[linear-gradient(90deg,hsl(340_80%_92%),hsl(320_60%_90%),hsl(280_50%_91%),hsl(260_45%_90%))] border-b border-[hsl(320_40%_85%)]",
        "dark:bg-[linear-gradient(90deg,hsl(340_40%_20%),hsl(320_30%_18%),hsl(280_25%_19%),hsl(260_25%_18%))] dark:border-[hsl(320_20%_30%)]",
        animatingOut && "opacity-0 -translate-y-2"
      )}
    >
      <div className="relative flex items-center justify-center gap-2 pr-8">
        <p className="text-xs text-foreground/90">
          <span className="font-semibold">{title}</span>
          <span className="ml-1.5">{description}</span>
          {" "}
          {onCtaClick ? (
            <button
              onClick={onCtaClick}
              className="font-semibold underline underline-offset-2 hover:opacity-80 transition"
            >
              {ctaLabel || t("wizards.seeDetails")}
            </button>
          ) : (
            <a
              href={ctaHref}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold underline underline-offset-2 hover:opacity-80 transition"
            >
              {ctaLabel || t("wizards.seeDetails")}
            </a>
          )}
        </p>
      </div>
      <button
        onClick={handleDismiss}
        className="absolute top-1/2 -translate-y-1/2 right-3 rounded-md p-0.5 text-foreground/50 hover:text-foreground hover:bg-black/5 dark:hover:bg-white/10 transition"
        aria-label={t("wizards.dismiss")}
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
